/**
 * COMPOSED 작성 페이지의 prefill → resolvedRows 추출 규칙.
 *
 * 페이지 컴포넌트에서 떼어낸 이유는 하나다 — **단독으로 검사할 수 있게 하려고**. 여기 규칙이
 * 틀리면 화면엔 에러가 아니라 "드롭다운이 옛 값을 내민다"로만 나타나서, 페이지를 통째로 띄우는
 * 테스트로는 잡히지 않는다.
 */
import {
  collectSortableSelectOptionSources,
  collectVariableOptionsSourceKeys,
} from '@safetyhub/report-composer';
import { isSuggestionDictKey } from '@safetyhub/shared';
import type { ReportTemplate } from '@safetyhub/shared';

/**
 * prefill 응답에서 카탈로그 list 후보 rows만 추출 — resolvedRows 주입 채널용(신규 prefill·편집
 * rows 주입·평가방식 재resolve 3경로 공유).
 * 대상 키 = 트리 소스(collectSortableSelectOptionSources) ∪ 변수 optionsFrom 소스
 * (collectVariableOptionsSourceKeys). includeDict면 '내 입력 누적' 사전 키(suggestion-dict:*)의
 * 배열도 포함(사전 먼저 — 소스 키와 겹치면 소스 rows가 정본).
 * resolvedRows 병합 방향(신규=주입분 보존 / 편집=키 단위 안전 병합 / 재resolve=서버 우선)은
 * 경로마다 달라 호출부가 소유한다.
 */
export function extractListRowsFromPrefill(
  prefill: Record<string, unknown>,
  tpl: ReportTemplate,
  opts?: { includeDict?: boolean },
): Record<string, Record<string, unknown>[]> {
  const listRows: Record<string, Record<string, unknown>[]> = {};
  if (opts?.includeDict) {
    for (const [key, value] of Object.entries(prefill)) {
      if (isSuggestionDictKey(key) && Array.isArray(value)) {
        listRows[key] = value as Record<string, unknown>[];
      }
    }
  }
  // 템플릿 dataSource 키는 제외한다 — 그 행들은 **작성자가 지금 편집 중인 초안**이고 정본은
  // values(신규=prefill seed / 편집=report.content)다. resolvedRows는 rc resolveSortableRows에서
  // values보다 **우선**이라(①>②), 여기 실어 보내면 후보가 초기 조립본에 얼어붙어 행을 고쳐도
  // 드롭다운은 옛 글자를 계속 내민다 — 그걸 고르면 낡은 값이 확정값·writes 전파로 되돌아온다.
  // (해당 사례: TBM 회의록 「중점위험요인 선정」 콤보의 후보 = 잠재위험요인 표 행.)
  const draftDatasetKeys = new Set((tpl.dataSources ?? []).map((d) => d.key));
  const sourceKeys = [
    ...new Set([
      ...collectSortableSelectOptionSources(tpl.composerWidgetTree ?? []),
      ...collectVariableOptionsSourceKeys(tpl.variables),
    ]),
  ].filter((key) => !draftDatasetKeys.has(key));
  for (const key of sourceKeys) {
    const value = prefill[key];
    if (Array.isArray(value)) listRows[key] = value as Record<string, unknown>[];
  }
  return listRows;
}
